import React from "react";
import { Helmet } from "react-helmet-async";
import { ArrowUpRight, Play, Mic, Video, Users, Monitor, Newspaper } from "lucide-react";

const CATEGORIES = [
  { label: "Podcasts", icon: Mic },
  { label: "Interviews", icon: Video },
  { label: "Conferences", icon: Monitor },
  { label: "Keynotes", icon: Play },
  { label: "Media Features", icon: Newspaper },
  { label: "Panel Discussions", icon: Users },
];

const ARCHIVE = [
  {
    year: "2025",
    title: "The Soul in the Machine: A Call for Ethical AI",
    venue: "Conversation with Angelique Letizia",
    kind: "Interview",
    url: "https://www.youtube.com/watch?v=nSCzvA8LqkQ",
    note: "On human dignity, sovereignty, and the limits of optimisation as a worldview.",
  },
];

export default function Media() {
  return (
    <>
      <Helmet>
        <title>Media &amp; Speaking — Ethical AI Now</title>
        <meta
          name="description"
          content="Ethical AI Now exists to ensure that technological progress remains aligned with human dignity, wisdom, responsibility, and human flourishing."
        />
        <meta
          name="keywords"
          content="AI Ethics, Human-Centred AI, AI Governance, Human Dignity and AI, Technology and Theology, Future of Artificial Intelligence, Ethical AI, Responsible AI, Matthew Kilkenny, Ethical AI Now"
        />
      </Helmet>

      <div className="bg-white text-[var(--eai-ink)]" data-testid="media-page">
        {/* Hero */}
        <section className="bg-[var(--eai-ink)] text-white py-20 lg:py-32 px-6 lg:px-12" data-testid="media-hero">
          <div className="max-w-[1400px] mx-auto">
            <p className="font-mono-eai text-[11px] uppercase tracking-widest text-[var(--eai-accent)]">
              Archive &middot; Public Record
            </p>
            <h1 className="mt-6 font-display text-5xl lg:text-7xl font-bold tracking-tight">
              Media &amp; Speaking
            </h1>
            <p className="mt-8 max-w-3xl text-xl lg:text-2xl text-white/80 leading-snug">
              Ethical AI Now exists to ensure that technological progress remains aligned with human dignity, wisdom, responsibility, and human flourishing.
            </p>
          </div>
        </section>

        {/* Categories */}
        <section className="border-b border-[var(--eai-border)] bg-white py-16 lg:py-20 px-6 lg:px-12" data-testid="media-categories">
          <div className="max-w-[1400px] mx-auto">
            <p className="overline">By format</p>
            <ul className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-[var(--eai-border)]">
              {CATEGORIES.map(({ label, icon: Icon }) => (
                <li
                  key={label}
                  className="bg-white p-6 flex flex-col items-start gap-4 hover:bg-[var(--eai-bg-2)] transition-colors duration-200"
                  data-testid={`media-category-${label.toLowerCase().replace(/ /g, "-")}`}
                >
                  <Icon size={22} strokeWidth={1.5} className="text-[var(--eai-accent)]" />
                  <span className="font-mono-eai uppercase tracking-[0.2em] text-xs">{label}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Archive */}
        <section className="border-b border-[var(--eai-border)] bg-[var(--eai-bg-2)] py-20 lg:py-28 px-6 lg:px-12" data-testid="media-archive">
          <div className="max-w-[1400px] mx-auto">
            <p className="overline">The Archive</p>
            <h2 className="mt-6 font-display text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl">
              Conversations on record &mdash; the public thinking behind the work.
            </h2>

            <ul className="mt-16 space-y-px bg-[var(--eai-border)]">
              {ARCHIVE.map((item) => (
                <li key={item.url} className="bg-white">
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="grid grid-cols-12 gap-6 p-8 lg:p-10 hover:bg-[var(--eai-bg-2)] transition-colors duration-200 group"
                    data-testid={`media-archive-item-${item.year}`}
                  >
                    <span className="col-span-2 font-mono-eai text-sm tracking-[0.2em] text-[var(--eai-accent)] pt-1">
                      {item.year}
                    </span>
                    <div className="col-span-8">
                      <p className="font-mono-eai uppercase tracking-[0.22em] text-xs text-[var(--eai-ink-2)]">
                        {item.kind} &middot; {item.venue}
                      </p>
                      <h3 className="mt-3 font-display text-2xl lg:text-3xl leading-snug">
                        {item.title}
                      </h3>
                      <p className="mt-3 text-base text-[var(--eai-ink-2)] max-w-2xl">{item.note}</p>
                    </div>
                    <span className="col-span-2 flex items-start justify-end text-[var(--eai-ink)] group-hover:text-[var(--eai-accent)]">
                      <ArrowUpRight size={28} strokeWidth={1.5} />
                    </span>
                  </a>
                </li>
              ))}
            </ul>

            <p className="mt-12 text-sm text-[var(--eai-ink-2)] max-w-2xl">
              New conversations are added as they are recorded. To invite Matthew Kilkenny to speak, brief, or sit for an interview, use the contact page.
            </p>
          </div>
        </section>
      </div>
    </>
  );
}
